import type { RelationTransactionOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useRelationTransactions } from "@/services";
import { dateUtils } from "@/utils/time";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import {
	Chip,
	DataTable,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

const TYPE_VARIANT = {
	order: "warning",
	payment: "success",
	adjustment: "info",
} as const;

const BuyerTransactionHistory = ({ relationId }: { relationId: string }) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const server = useServerTableState();
	const { data, isLoading, isFetching, error, refetch } =
		useRelationTransactions({
			path: { relation_id: relationId },
			query: { page: server.pageIndex + 1, page_size: server.pageSize },
		});

	const columns = useMemo<ColumnDef<RelationTransactionOutput>[]>(
		() => [
			{
				id: "type",
				header: t("relation.detail.transactionType"),
				enableSorting: false,
				meta: { className: "w-[12%]" },
				cell: ({ row }) => (
					<Chip variant={TYPE_VARIANT[row.original.type]} size="sm">
						{t(`relation.detail.types.${row.original.type}`)}
					</Chip>
				),
			},
			{
				id: "debt_change",
				header: t("relation.detail.change"),
				enableSorting: false,
				meta: { className: "w-[16%]" },
				cell: ({ row }) => {
					const change = Number(row.original.debt_change);
					return (
						<span
							className={change > 0 ? "text-danger-text" : "text-success-text"}
						>
							{change > 0 ? "+" : "−"}
							{formatNumber(Math.abs(change), "price")}
						</span>
					);
				},
			},
			{
				id: "debt_after",
				header: t("relation.detail.debtAfter"),
				enableSorting: false,
				meta: { className: "w-[18%]" },
				cell: ({ row }) => formatNumber(row.original.debt_after, "price"),
			},
			{
				id: "note",
				header: t("relation.detail.note"),
				enableSorting: false,
				meta: { className: "w-[34%] max-w-0" },
				cell: ({ row }) => {
					const value = row.original.note ?? row.original.reference;
					if (!value) return "-";
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="block w-full truncate">{value}</span>
							</TooltipTrigger>
							<TooltipContent>{value}</TooltipContent>
						</Tooltip>
					);
				},
			},
			{
				id: "created_at",
				header: t("relation.detail.date"),
				enableSorting: false,
				meta: { className: "w-[20%]" },
				cell: ({ row }) => {
					const created = new Date(row.original.created_at);
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="block w-full truncate whitespace-nowrap">
									{getTimeSinceText(created, t, lang)}
								</span>
							</TooltipTrigger>
							<TooltipContent>
								{dateUtils.format(created, "d MMM yyyy, HH:mm")}
							</TooltipContent>
						</Tooltip>
					);
				},
			},
		],
		[t, lang],
	);

	return (
		<div className="mt-4 flex flex-col gap-3">
			<p className="text-sm text-secondary-text-100">
				{t("relation.detail.transactionHint")}
			</p>
			<DataTable
				className="[&_table]:table-fixed"
				columns={columns}
				data={data?.results ?? []}
				totalItems={data?.count ?? 0}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => `${row.type}-${row.id}`}
			/>
		</div>
	);
};

export default BuyerTransactionHistory;
